import React, { useState } from 'react';
import {
  BadgePercentIcon,
  GiftIcon,
  SackDollarIcon,
  VideoIcon,
  FileIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@revfluence/fresh-icons/solid/esm';
import { addDays, format } from 'date-fns';
import { Button } from '@/shadcn/components/ui/button';
import { ContentType, GuidelineAttachmentInput } from '@/app/types/globalTypes';
import { GetMemberApplicantInfo_memberApplicantInfo_briefTemplate_agreementIteration } from '@/app/queries/types/GetMemberApplicantInfo';
import { POST_TYPE_NAME, DeliverablesIcon } from '@/app/containers/Projects/SettingsPage/Recruitment/constants';

interface BriefSectionProps {
  deliverables: string;
  compensation: {
    hasAny: boolean;
    product?: string;
    payment?: string;
    commission?: string;
  };
  onViewBrief: () => void;
  // Extended props for expanded view
  briefTemplate?: GetMemberApplicantInfo_memberApplicantInfo_briefTemplate_agreementIteration | null;
}

export const BriefSection: React.FC<BriefSectionProps> = ({
  deliverables,
  compensation,
  onViewBrief,
  briefTemplate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderCompensationRows = () => {
    if (!compensation.hasAny) {
      return (
        <div className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Compensation
          </div>
          <div className="text-grey-6 text-sm font-normal">
            No Compensation
          </div>
        </div>
      );
    }

    const rows = [];

    if (compensation.product) {
      rows.push(
        <div key="product" className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Free Product
          </div>
          <div className="text-grey-6 text-sm font-normal">
            {compensation.product}
          </div>
        </div>
      );
    }

    if (compensation.payment) {
      rows.push(
        <div key="payment" className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Payment
          </div>
          <div className="text-grey-6 text-sm font-normal">
            {compensation.payment}
          </div>
        </div>
      );
    }

    if (compensation.commission) {
      rows.push(
        <div key="commission" className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Commission
          </div>
          <div className="text-grey-6 text-sm font-normal">
            {compensation.commission}
          </div>
        </div>
      );
    }

    return rows;
  };

  const getBriefPerks = () => {
    if (!briefTemplate) return [];

    const perks = [];
    if (briefTemplate.offersPayment && briefTemplate.price > 0) {
      perks.push({
        title: 'Payment',
        amount: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(briefTemplate.price),
        icon: <SackDollarIcon className="w-3 h-3" />
      });
    }
    if (briefTemplate.offersProduct) {
      perks.push({
        title: 'Free Product',
        details: briefTemplate.productDescription || 'Free Product',
        icon: <GiftIcon className="w-3 h-3" />
      });
    }
    if (briefTemplate.offersCommission) {
      perks.push({
        title: 'Commission',
        details: briefTemplate.commissionDescription || 'Commission Available',
        icon: <BadgePercentIcon className="w-3 h-3" />
      });
    }
    return perks;
  };

  const getDeliverables = () => {
    if (!briefTemplate?.contentGuidelines) return [];

    const allContentTypes = new Set<ContentType>();
    briefTemplate.contentGuidelines.forEach((guideline) => {
      if (guideline.acceptedPostTypes?.length) {
        guideline.acceptedPostTypes.forEach((type) => allContentTypes.add(type));
      } else if (guideline.postType) {
        allContentTypes.add(guideline.postType);
      }
    });

    return Array.from(allContentTypes).map((type) => ({
      type,
      name: POST_TYPE_NAME[type] || type,
    }));
  };

  const getMappedDueDates = () => {
    if (!briefTemplate?.contentGuidelines) return [];

    const dueDates = new Set<string>();
    briefTemplate.contentGuidelines.forEach((guideline) => {
      if (guideline.dueDates) {
        guideline.dueDates.forEach((dueDate) => {
          if (dueDate?.submission?.dueDateInDays) {
            dueDates.add(format(addDays(new Date(), dueDate.submission.dueDateInDays), 'MM/dd/yyyy'));
          }
        });
      }
    });
    return Array.from(dueDates);
  };

  const getDeliverableGuidelines = () => {
    if (!briefTemplate?.contentGuidelines) return [];

    return briefTemplate.contentGuidelines
      .map((guideline) => ({
        title: guideline.label || POST_TYPE_NAME[guideline.postType] || guideline.postType,
        instructions: guideline.guidelines?.flatMap((g) => g.instructions) || [],
      }))
      .filter((guideline) => guideline.instructions.length > 0);
  };

  const getAllAttachments = () => {
    if (!briefTemplate?.contentGuidelines) return [];

    const attachments: GuidelineAttachmentInput[] = [];
    briefTemplate.contentGuidelines.forEach((guideline) => {
      if (guideline.attachments) {
        attachments.push(...guideline.attachments);
      }
    });
    return attachments;
  };

  const handleViewBrief = () => {
    if (briefTemplate) {
      setIsExpanded(!isExpanded);
    } else {
      onViewBrief();
    }
  };

  const briefPerks = getBriefPerks();
  const deliverablesList = getDeliverables();
  const mappedDueDates = getMappedDueDates();
  const deliverableGuidelines = getDeliverableGuidelines();
  const attachments = getAllAttachments();

  return (
    <div className="bg-white rounded-2xl border-t-4 border-purple-4 border overflow-hidden">
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <div className="flex-1 text-grey-6 text-base font-medium">
            Brief
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-grey-6 text-xs font-medium hover:bg-grey-1 flex items-center gap-1"
            onClick={handleViewBrief}
          >
            {briefTemplate ? (
              <>
                {isExpanded ? 'Hide Brief' : 'View Brief'}
                {isExpanded ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
              </>
            ) : (
              'View Brief'
            )}
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Deliverables
          </div>
          <div className="text-grey-6 text-sm font-normal">
            {deliverables}
          </div>
        </div>

        {renderCompensationRows()}
      </div>

      {/* Simple expanded brief details */}
      {isExpanded && briefTemplate && (
        <div className="border-t border-grey-2 p-4">
          <div className="max-h-96 overflow-y-auto space-y-4">
            {/* Perks Section */}
            {briefPerks.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3">Perks</h4>
                <div className="space-y-3">
                  {briefPerks.map((perk) => (
                    <div key={perk.title} className="flex items-center gap-3">
                      {perk.icon}
                      <div className="flex-1">
                        <div className="text-sm font-medium">{perk.title}</div>
                        <div className="text-sm text-grey-5">{perk.amount || perk.details}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deliverables Section */}
            {deliverablesList.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3">Deliverables</h4>
                <div className="space-y-3">
                  <div className="text-sm">
                    You may choose to submit <span className="font-medium">any</span> of the following content types:
                  </div>
                  {deliverablesList.map((deliverable) => (
                    <div key={deliverable.name} className="flex items-center gap-3">
                      <DeliverablesIcon contentType={deliverable.type} />
                      <div className="text-sm">{deliverable.name}</div>
                    </div>
                  ))}
                  {mappedDueDates.length > 0 && (
                    <div>
                      <span className="text-sm font-medium">Deliverables due by:</span>{' '}
                      <span className="text-sm text-grey-5">{mappedDueDates.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content Guidelines Section */}
            {(deliverableGuidelines.length > 0 || attachments.length > 0) && (
              <div>
                <h4 className="text-sm font-medium mb-3">Content Guidelines</h4>
                <div className="space-y-3">
                  {deliverableGuidelines.map((guideline) => (
                    <div key={guideline.title}>
                      <div className="text-sm font-medium mb-2">{guideline.title}</div>
                      <ul className="list-disc pl-5 space-y-1">
                        {guideline.instructions.map((instruction, index) => (
                          <li key={index} className="text-sm">
                            {instruction}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {attachments.length > 0 && (
                    <div>
                      <div className="text-sm font-medium mb-2">Attachments:</div>
                      {attachments.map((attachment, index) => (
                        <a
                          key={`${attachment.filename}-${index}`}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-500 hover:underline"
                        >
                          {attachment.type?.startsWith('video/') ?
                            <VideoIcon className="w-4 h-4" /> :
                            <FileIcon className="w-4 h-4" />}
                          <span>{attachment.filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content Usage Rights Section */}
            {briefTemplate.contentUsageRights && (
              <div>
                <h4 className="text-sm font-medium mb-3">Content Usage Rights</h4>
                <div className="text-sm">{briefTemplate.contentUsageRights}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
